import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import InfoIconTip, { closeTipGroup } from '@intelligence/components/InfoIconTip';
import { getLocaleText } from '@intelligence/components/GenreSelect/utils';

import type {
  IBaseProps,
  IGenreSourceOption,
  IGenreSource,
} from '@intelligence/components/GenreSelect/types';

import '@intelligence/components/GenreSelect/components/SourceSelect/index.scss';

const GENRE_SOURCE_TIP_GROUP = 'genre-source-tabs';

interface ISourceSelectProps extends IBaseProps {
  options: IGenreSourceOption[];
  value: IGenreSource;
  onChange: (value: IGenreSource) => void;
}

/**
 * 这里由于数据源类型数量不是固定的，所以没办法在css里定义flex-basis的值，但是如果不定义flex-basis的值，每个数据源类型的宽度就不能固定了（宽度会根据内容的不同而不同）
 * 所以这里还是得通过js来赋值，暂时没想到优化的方法了
 * @param length 数据源类型的数量
 * @returns flex-basis的值
 */
export const getAverageFlexBasis = (length?: number): string =>
  !!length ? `${Math.floor((1 / length) * 100)}%` : '0%';

const getGenreSourceIcon = (
  isSingle: boolean,
  isActive: boolean,
  source: IGenreSourceOption,
): string | undefined => {
  const sourceIcon = source?.icon;
  if (isSingle) return source?.actionIcon;
  return isActive ? source?.actionIcon : sourceIcon;
};

const SourceSelect: React.FC<ISourceSelectProps> = (props) => {
  const { style, className, options, value, onChange, tipTextOutside } = props;

  const { locale } = useIntl();

  const optionsLength = options?.length;
  const isSingle = optionsLength === 1;
  return (
    <div style={style} className={classnames('genre-source-selector', className)}>
      <ul
        className="genre-sources"
        onMouseLeave={() => closeTipGroup(GENRE_SOURCE_TIP_GROUP)}
      >
        {options?.map((option) => {
          const {
            value: optionValue,
            remarks: optionRemarksEN,
            remarksCN: optionRemarksCN,
          } = option || {};
          const isActive = optionValue === value;
          const isMultipleAndActive = !isSingle && isActive;
          const optionIcon = getGenreSourceIcon(isSingle, isActive, option);
          const optionText = getLocaleText(locale, option);
          const optionRemarks = getLocaleText(locale, {
            text: tipTextOutside ? tipTextOutside?.remarks : optionRemarksEN,
            textCN: tipTextOutside ? tipTextOutside?.remarksCN : optionRemarksCN,
          });
          return (
            <li
              key={optionValue}
              style={{ flexBasis: getAverageFlexBasis(optionsLength) }}
              className={classnames('genre-source', {
                active: isMultipleAndActive,
                'single-source': isSingle,
              })}
              onClick={(e) => {
                e.stopPropagation();
                if (isSingle || isActive) return;
                onChange?.(optionValue);
              }}
            >
              <img className="genre-source-icon" src={optionIcon} alt={optionText} />
              <span>{optionText}</span>
              {optionRemarks && (
                <InfoIconTip
                  tipText={optionRemarks}
                  selected={isMultipleAndActive || isSingle}
                  maxWidth={tipTextOutside ? 442 : undefined}
                  className="card-slot-v2-right-tip"
                  groupId={GENRE_SOURCE_TIP_GROUP}
                />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default SourceSelect;
